import { lowerCamelCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { isNumberScanValue, ScanTemplateValue } from '@kaiizen/kai-scan-template-system';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactNode, useState } from 'react';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { Button, LabeledInput, LabeledMultiSelect, LabeledSelect } from '../../Widgets';
import { MAX_NUMBER_INPUT_LENGTH, MAX_VALUE_EQUATION_LENGTH, MAX_VALUE_NAME_LENGTH } from './scanTemplateFieldLimits';
import { normalizeTemplateValueInput, parseTemplateNumericInput } from './ScanTemplateValueInputLib';

const CONTAINER_STYLE = css`
  label: CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

const VALUE_ITEM_STYLE = css`
  label: VALUE_ITEM_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  padding-bottom: ${LAYOUT2.spacing2}px;
  border-bottom: 1px solid var(--border1);
`;

const VALUE_ITEM_NAME_ROW_STYLE = css`
  label: VALUE_ITEM_NAME_ROW_STYLE;
  display: grid;
  grid-template-columns: 1fr 100px;
  gap: ${LAYOUT2.spacing1}px;
  align-items: center;
`;

const VALUE_ITEM_NAME_ROW_WITH_ACTION_STYLE = css`
  label: VALUE_ITEM_NAME_ROW_WITH_ACTION_STYLE;
  grid-template-columns: 1fr 100px 44px;
`;

const VALUE_ITEM_EQUATION_ROW_STYLE = css`
  label: VALUE_ITEM_EQUATION_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  align-items: center;

  > * {
    flex: 1;
    min-width: 0;
  }
  > *:first-of-type {
    flex: 2;
  }
`;

const DELETE_BUTTON_STYLE = css`
  label: DELETE_BUTTON_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text4);
  transition: color 0.2s ease;

  &:hover {
    color: var(--red);
  }
`;

const ADD_VALUE_FORM_STYLE = css`
  label: ADD_VALUE_FORM_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

const BUTTON_CONTAINER_STYLE = css`
  label: BUTTON_CONTAINER_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
`;

const EMPTY_STATE_STYLE = css`
  label: EMPTY_STATE_STYLE;
  ${TYPOGRAPHY2.small};
  padding: ${LAYOUT2.spacing1}px 0;
  color: var(--text4);
`;

const VALUE_TYPES = [
  { value: 'text', label: 'text' },
  { value: 'count', label: 'count' },
  { value: 'usd', label: 'usd' },
  { value: 'sqft', label: 'sqft' },
  { value: 'ft', label: 'ft' },
  { value: 'in', label: 'in' },
  { value: 'acres', label: 'acres' },
  { value: 'fahrenheit', label: 'fahrenheit' },
];

// '' represents "no meaning" (meaningType: undefined)
const MEANING_TYPES = [
  { value: '', label: 'None' },
  { value: 'flooringAreaMeasurement', label: 'Flooring area' },
];

/** A min or max as typed: blank clears it, and anything that isn't a number keeps the current one. */
const getUpdatedBound = (typed: string, current: number | undefined): number | undefined => {
  const trimmed = typed.trim();
  return trimmed === '' ? undefined : parseTemplateNumericInput(trimmed) ?? current;
};

/**
 * The fields of one template value: name, type, default or equation, meaning, a number's min and
 * max, and a text value's pick list options. Adding a value and editing one share this form.
 */
const ValueFields = ({
  valueKey,
  value,
  onUpdate,
  onRename,
  readOnly,
  autoFocus,
  nameRowAction,
}: {
  valueKey: string;
  value: ScanTemplateValue;
  onUpdate: (updates: Partial<ScanTemplateValue>) => void;
  onRename: (newKey: string) => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  /** Sits at the end of the name row, such as an existing value's delete button. */
  nameRowAction?: ReactNode;
}) => {
  const isTextType = value.type === 'text';
  const valueOptions = value.valueOptions || [];
  const { featureFlags: { useRoomMeasurements } } = useFeatureFlags();

  // a text value has pick list options and a number has bounds; switching type drops the other kind's
  const handleTypeChange = (newType: string) =>
    onUpdate(newType === 'text' ? { type: newType, minValue: undefined, maxValue: undefined } : { type: newType, valueOptions: undefined });

  const handleOptionsChange = (selectedValues: string[]) =>
    onUpdate({ valueOptions: selectedValues.length === 0 ? undefined : selectedValues.map(val => ({ value: val })) });

  // Extract just the values from valueOptions for LabeledMultiSelect
  const selectedOptionValues = valueOptions.map(opt => opt.value?.toString() ?? '');
  const optionItems = valueOptions.map(opt => ({
    value: opt.value?.toString() ?? '',
    label: opt.label,
  }));

  return (
    <>
      <div css={[VALUE_ITEM_NAME_ROW_STYLE, !!nameRowAction && VALUE_ITEM_NAME_ROW_WITH_ACTION_STYLE]}>
        <LabeledInput
          size='small'
          label='Name'
          value={valueKey}
          disabled={readOnly}
          maxLength={MAX_VALUE_NAME_LENGTH}
          onUpdate={onRename}
          placeholder='valueName'
          autoFocus={autoFocus}
        />
        <LabeledSelect
          label='Type'
          size='small'
          disabled={readOnly}
          value={value.type ?? 'count'}
          items={VALUE_TYPES}
          onUpdate={handleTypeChange}
        />
        {nameRowAction}
      </div>
      <div css={VALUE_ITEM_EQUATION_ROW_STYLE}>
        <LabeledInput
          size='small'
          label='Default / Equation'
          disabled={readOnly}
          value={value.value?.toString() ?? ''}
          maxLength={MAX_VALUE_EQUATION_LENGTH}
          onUpdate={newValue => onUpdate({ value: normalizeTemplateValueInput(newValue, value.type) })}
          placeholder='Enter value or equation'
        />
        {useRoomMeasurements && (
          <LabeledSelect
            label='Meaning'
            size='small'
            disabled={readOnly}
            value={value.meaningType ?? ''}
            items={MEANING_TYPES}
            onUpdate={newMeaning => onUpdate({ meaningType: newMeaning === '' ? undefined : newMeaning as ScanTemplateValue['meaningType'] })}
          />
        )}
        {isNumberScanValue(value) && (
          <>
            <LabeledInput
              size='small'
              label='Min'
              disabled={readOnly}
              value={value.minValue?.toString() ?? ''}
              maxLength={MAX_NUMBER_INPUT_LENGTH}
              onUpdate={newValue => onUpdate({ minValue: getUpdatedBound(newValue, value.minValue) })}
              placeholder='No minimum'
            />
            <LabeledInput
              size='small'
              label='Max'
              disabled={readOnly}
              value={value.maxValue?.toString() ?? ''}
              maxLength={MAX_NUMBER_INPUT_LENGTH}
              onUpdate={newValue => onUpdate({ maxValue: getUpdatedBound(newValue, value.maxValue) })}
              placeholder='No maximum'
            />
          </>
        )}
      </div>
      {isTextType && (
        <LabeledMultiSelect
          size='small'
          label='Options'
          value={selectedOptionValues}
          items={optionItems}
          allowAddNew={!readOnly}
          placeholder='Add options...'
          onChange={handleOptionsChange}
          disabled={readOnly}
        />
      )}
    </>
  );
};

const ValueItem = ({
  valueKey,
  value,
  onUpdate,
  onRename,
  onDelete,
  readOnly,
}: {
  valueKey: string;
  value: ScanTemplateValue;
  onUpdate: (updates: Partial<ScanTemplateValue>) => void;
  onRename: (newKey: string) => void;
  onDelete: () => void;
  readOnly?: boolean;
}) => (
  <div css={VALUE_ITEM_STYLE}>
    <ValueFields
      valueKey={valueKey}
      value={value}
      onUpdate={onUpdate}
      onRename={newKey => {
        const normalized = lowerCamelCase(newKey.trim());
        if (normalized && normalized !== valueKey) onRename(normalized);
      }}
      readOnly={readOnly}
      nameRowAction={!readOnly && (
        <button css={DELETE_BUTTON_STYLE} onClick={onDelete} title='Delete value'>
          <DeleteIcon fontSize='small' />
        </button>
      )}
    />
  </div>
);

const AddValueForm = ({
  onAdd,
  onCancel,
}: {
  onAdd: (key: string, value: ScanTemplateValue) => void;
  onCancel: () => void;
}) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState<ScanTemplateValue>({ type: 'count' });

  const handleAdd = () => {
    const normalizedKey = lowerCamelCase(newKey.trim());
    if (!normalizedKey) {
      alert('Please enter a name for the value');
      return;
    }
    // the default was normalized for the type it was typed under; the type may have changed since
    onAdd(normalizedKey, { ...newValue, value: normalizeTemplateValueInput(newValue.value?.toString() ?? '', newValue.type) });
  };

  return (
    <div css={ADD_VALUE_FORM_STYLE}>
      <ValueFields
        valueKey={newKey}
        value={newValue}
        onUpdate={updates => setNewValue(current => ({ ...current, ...updates }))}
        onRename={setNewKey}
        autoFocus
      />
      <div css={BUTTON_CONTAINER_STYLE}>
        <Button onClick={handleAdd}>Add</Button>
        <Button buttonStyle='gray' onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export const ScanTemplateAdvancedValuesEditor = ({
  values,
  onUpdateValues,
  readOnly,
}: {
  values?: Record<string, ScanTemplateValue>;
  onUpdateValues: (updates: Record<string, ScanTemplateValue>) => void;
  readOnly?: boolean;
}) => {
  const [isAddingValue, setIsAddingValue] = useState(false);

  const sortedKeys = Object.keys(values ?? {}).sort((a, b) => a.localeCompare(b));

  const handleUpdateValue = (key: string, updates: Partial<ScanTemplateValue>) => {
    if (!values?.[key]) return;
    onUpdateValues({
      [key]: { ...values[key], ...updates },
    });
  };

  const handleRenameValue = (oldKey: string, newKey: string) => {
    if (!values?.[oldKey]) return;
    if (values[newKey]) {
      alert(`A value with the name "${newKey}" already exists`);
      return;
    }
    onUpdateValues({
      [oldKey]: null as any, // Remove old key
      [newKey]: values[oldKey], // Add with new key
    });
  };

  const handleDeleteValue = (key: string) => {
    if (!confirm(`Are you sure you want to delete the value "${key}"?`)) return;
    onUpdateValues({
      [key]: null as any, // Setting to undefined removes the value
    });
  };

  const handleAddValue = (key: string, value: ScanTemplateValue) => {
    if (values?.[key]) {
      alert(`A value with the name "${key}" already exists`);
      return;
    }
    onUpdateValues({
      [key]: value,
    });
    setIsAddingValue(false);
  };

  return (
    <div css={CONTAINER_STYLE}>
      {sortedKeys.length === 0 && !isAddingValue && (
        <div css={EMPTY_STATE_STYLE}>
          No values defined. Click "Add Value" to create one.
        </div>
      )}

      {sortedKeys.map(key => (
        <ValueItem
          key={key}
          valueKey={key}
          value={values![key]}
          onUpdate={updates => handleUpdateValue(key, updates)}
          onRename={newKey => handleRenameValue(key, newKey)}
          onDelete={() => handleDeleteValue(key)}
          readOnly={readOnly}
        />
      ))}

      {!readOnly && isAddingValue && (
        <AddValueForm
          onAdd={handleAddValue}
          onCancel={() => setIsAddingValue(false)}
        />
      )}

      {!readOnly && !isAddingValue && <Button onClick={() => setIsAddingValue(true)}>Add Value</Button>}
    </div>
  );
};
